import { useMemo, useState } from "react";
import { Search, Check } from "lucide-react";
import { OTHER_OPTION } from "./onboardingData";
import { primaryBtn, textInput } from "./onboardingStyles";

// ─── SEARCH PICKER ─────────────────────────────────────────────────────────────
// Used for the course and job-title lists (the long, second-level lists) —
// a search bar filters the list live; when not searching, items are grouped
// under letter headers (no jump strip — removed in favor of just scrolling).
// "Other" is always pinned at the bottom, outside the alphabet, and reveals a
// free-text field instead of auto-advancing (there's nothing to look up).
function OnboardingSearchPicker({ items, selected, onSelect, otherText, onOtherTextChange, onOtherSubmit, placeholder }) {
  const [query, setQuery] = useState("");
  const [otherOpen, setOtherOpen] = useState(selected === OTHER_OPTION);

  const sorted = useMemo(() => [...items].sort((a, b) => a.localeCompare(b)), [items]);
  const grouped = useMemo(() => {
    const g = {};
    sorted.forEach((it) => {
      const letter = it[0].toUpperCase();
      (g[letter] = g[letter] || []).push(it);
    });
    return g;
  }, [sorted]);

  const q = query.trim().toLowerCase();
  const filtered = q ? sorted.filter((it) => it.toLowerCase().includes(q)) : null;

  const rowStyle = (active) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
    textAlign: "left", padding: "13px 14px", borderRadius: 14, border: "none",
    background: active ? "var(--c-bg-active, #fdeef4)" : "transparent",
    color: "var(--c-text, #333)",
    fontFamily: "inherit", fontSize: 14.5, fontWeight: active ? 700 : 600, cursor: "pointer",
  });

  const pick = (val) => {
    if (val === OTHER_OPTION) { setOtherOpen(true); return; }
    setOtherOpen(false);
    onSelect(val);
  };

  const Row = ({ label }) => (
    <button style={rowStyle(selected === label)} onClick={() => pick(label)}>
      {label}
      {selected === label && <Check size={16} strokeWidth={3} />}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 12 }}>
      <div style={{ position: "relative" }}>
        <Search size={17} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--c-text-soft, #999)" }} />
        <input
          placeholder={placeholder || "Search…"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ ...textInput, paddingLeft: 40 }}
        />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {filtered ? (
          filtered.length ? filtered.map((it) => <Row key={it} label={it} />) : (
            <div style={{ padding: "16px 4px", fontSize: 13.5, color: "var(--c-text-soft, #999)" }}>No matches. Try “Other” below.</div>
          )
        ) : (
          Object.keys(grouped).sort().map((letter) => (
            <div key={letter}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: "var(--c-text-soft, #999)", letterSpacing: 0.5, padding: "10px 6px 4px" }}>{letter}</div>
              {grouped[letter].map((it) => <Row key={it} label={it} />)}
            </div>
          ))
        )}

        <div style={{ marginTop: 8, borderTop: "1.5px dashed var(--c-border-mid, #eee)", paddingTop: 8 }}>
          <Row label={OTHER_OPTION} />
          {otherOpen && (
            <div style={{ padding: "6px 14px 4px", display: "flex", flexDirection: "column", gap: 10 }}>
              <input
                autoFocus
                placeholder="Type your own"
                value={otherText}
                onChange={(e) => onOtherTextChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && otherText.trim()) onOtherSubmit(); }}
                style={textInput}
              />
              <button style={primaryBtn(!otherText.trim())} disabled={!otherText.trim()} onClick={onOtherSubmit}>Continue</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingSearchPicker;
